/**
 * Memory refs `M-n` (wave C, decision 0071, spec §4.2). A new memory gets its ref in decide (on create, from
 * `refCounters.M`, carried in `MemoryItemCreated` like `O-n`); a revision and a restore keep it. Items from before the
 * wave are numbered by {@link numberMemory} (no memory exists in production, so this is a safety net for snapshots and
 * tests). Pure.
 */
import type { Ids, MemoryDraft, MemoryItem, ProjectState } from "../types"

/** The memory ref for number `n`. */
export const memoryRef = (n: number): Ids["ShortRef"] => `M-${n}` as Ids["ShortRef"]

/**
 * Number a state's memory items that have no ref, in array (= creation) order, after the highest number already used;
 * sets `refCounters.M`. A no-op (the same object) when `refCounters.M` is already set or there is no memory.
 */
export const numberMemory = (s: ProjectState): ProjectState => {
  if (s.refCounters.M !== undefined || s.memory.length === 0) return s
  let n = 0
  for (const x of s.memory) {
    const m = x.ref ? /^M-(\d+)$/.exec(x.ref) : null
    if (m) n = Math.max(n, Number(m[1]))
  }
  const memory = s.memory.map((x) => (x.ref ? x : { ...x, ref: memoryRef(++n) }))
  return { ...s, memory, refCounters: { ...s.refCounters, M: n } }
}

/** The memory item with this ref ("M-7"), in any status (a forgotten one still opens by ref). */
export const memoryByRef = (s: ProjectState, ref: string): MemoryItem | undefined =>
  s.memory.find((x) => x.ref === ref)

/**
 * Has a person written or checked this memory (spec §4.5)? The one definition (the pack's index, `read{M-n}` and
 * search use it; the web's `isUnchecked` mirrors it). Rules and preferences count as people's (an agent's rule is
 * always a card a person accepted). A fact or learning is checked when a person accepted its card or changed its text
 * in Minga (`reviewedBy` a person; Forget, Restore and an Edit of other fields keep it), or when a person wrote it in
 * Minga and no agent's run has written its text since (`sourceRunId` null). An agent's revision of a person's fact
 * (`remember{replaces}`, which sets `sourceRunId`) leaves it unchecked, and so does a person's CLI
 * (`via: person_mcp`): the pack lists those as information.
 */
export const memoryChecked = (
  m: Pick<MemoryItem, "content" | "createdBy" | "via" | "reviewedBy" | "sourceRunId">,
): boolean => {
  if (m.content._tag !== "fact" && m.content._tag !== "learning") return true
  if (m.reviewedBy?._tag === "user") return true
  return m.createdBy._tag === "user" && m.via === undefined && m.sourceRunId === null
}

/** The kinds people and `remember` write in wave C. */
export type TeachKind = "rule" | "preference" | "fact" | "learning"

/**
 * The kind Teach and Remember this guess from the text (spec §4.4): a rule when it starts with always / never / don't,
 * a preference when it says how someone likes things, else a fact. People change it with a chip after saving.
 */
export const guessMemoryKind = (text: string): TeachKind => {
  const t = text.trim().toLowerCase()
  if (/^(always|never|don't|do not|dont)\b/.test(t)) return "rule"
  if (/\b(prefers?|likes?|wants?|would rather|reviews? .* on (his|her|their) phone)\b/.test(t))
    return "preference"
  return "fact"
}

const DAY_MS = 86_400_000
/** A learning a person writes is revisited after this long (spec §4.2; editable on the memory page). */
export const LEARNING_REVISIT_MS = 90 * DAY_MS
const LINE_MAX = 160

const firstLineOf = (text: string): string => {
  const line = (text.split("\n").find((l) => l.trim() !== "") ?? "").trim()
  return line.length > LINE_MAX ? `${line.slice(0, LINE_MAX - 1)}…` : line
}

/**
 * The draft a person's write (Teach, Remember this, the person MCP's `remember`) sends, with the defaults of spec §4.2
 * the schema needs: the title and a fact's summary are the first line (≤ 160 characters); a fact's and a learning's
 * evidence is "said by <person>" (a statement); a learning is medium confidence, revisited in 90 days.
 */
export const memoryDraftOf = (input: {
  readonly kind: TeachKind
  readonly text: string
  readonly title?: string
  readonly scope: MemoryDraft["scope"]
  readonly about?: Ids["UserId"]
  readonly source?: MemoryDraft["source"]
  readonly by: Ids["UserId"]
  readonly now: number
}): MemoryDraft => {
  const title = (input.title?.trim() || firstLineOf(input.text)).slice(0, LINE_MAX)
  const evidence = [{ _tag: "statement" as const, userId: input.by, at: input.now }] as const
  const content: MemoryDraft["content"] =
    input.kind === "rule"
      ? { _tag: "rule" }
      : input.kind === "preference"
        ? { _tag: "preference", ...(input.about !== undefined ? { about: input.about } : {}) }
        : input.kind === "fact"
          ? { _tag: "fact", summary: title, evidence }
          : { _tag: "learning", evidence, confidence: "medium", revisitAt: input.now + LEARNING_REVISIT_MS }
  return {
    scope: input.scope,
    title,
    body: input.text.trim(),
    content,
    triggers: { tags: [], paths: [], integrations: [] },
    locked: false,
    ...(input.source !== undefined ? { source: input.source } : {}),
  }
}
